import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Modal,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  MailOutlined,
  PhoneOutlined,
  ReloadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import CopyableText from "common/CopyableText";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useUsers from "hooks/userManagement/useUsers";
import { useFileUpload } from "hooks/useFileUpload";
import {
  fetchWorkspace,
  fetchWorkspaceMembers,
} from "services/workspaceService";
import paths from "routes/paths";
import {
  formatDateTime,
  membershipUserEmail,
  membershipUserId,
  membershipUserLabel,
  readable,
  statusColor,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";

const { Text, Title } = Typography;

const cleanValue = (value) => (value == null ? "" : String(value).trim());

const readPhotoUrl = async (value, getFileMetadata) => {
  const raw = cleanValue(value);
  if (!raw) return null;
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  if (!getFileMetadata) return null;

  try {
    const meta = await getFileMetadata(raw);
    return meta?.publicUrl || meta?.url || meta?.fileUrl || null;
  } catch {
    return null;
  }
};

const displayName = (user, membership) => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.name || membershipUserLabel(membership) || user?.email || "User";
};

const WorkspaceMemberDetailsPage = () => {
  const { workspaceId, userId } = useParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const actorId = authUser?.id || authUser?.userId || "";
  const { getUserById } = useUsers(0, 20, null, true, { autoFetch: false });
  const { getFileMetadata } = useFileUpload();
  const [account, setAccount] = useState(null);
  const [membership, setMembership] = useState(null);
  const [memberUser, setMemberUser] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoPreviewOpen, setPhotoPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    if (!workspaceId || !userId || !actorId) return;

    try {
      setLoading(true);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount || {});

      let nextMembership =
        (nextAccount?.users || []).find((item) => membershipUserId(item) === userId) || null;

      try {
        const page = await fetchWorkspaceMembers({
          actorId,
          workspaceId: workspaceId,
          size: 100,
        });
        nextMembership =
          (page.content || []).find((item) => membershipUserId(item) === userId) ||
          nextMembership;
      } catch {
        // Account payload membership is enough for the minimal view.
      }

      setMembership(nextMembership);

      let nextUser = null;
      try {
        nextUser = await getUserById(userId);
      } catch {
        nextUser = nextMembership?.user || { id: userId };
      }
      setMemberUser(nextUser);
      setPhotoUrl(
        await readPhotoUrl(
          nextUser?.profilePhoto ||
            nextUser?.photo ||
            nextUser?.avatar ||
            nextMembership?.user?.profilePhoto ||
            nextMembership?.user?.photo ||
            nextMembership?.user?.avatar,
          getFileMetadata,
        ),
      );
    } catch (error) {
      message.error(error?.message || "Failed to load workspace member.");
    } finally {
      setLoading(false);
    }
  }, [actorId, getFileMetadata, getUserById, workspaceId, userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const roles = membership?.roleNames || [];
  const privileges = membership?.privilegeScopes || [];
  const email = memberUser?.email || membershipUserEmail(membership);
  const phone = cleanValue(memberUser?.phone);
  const gender = cleanValue(memberUser?.gender);
  const name = useMemo(() => displayName(memberUser, membership), [memberUser, membership]);
  const membershipStatus = readable(membership?.membershipStatus, "Membership");
  const userStatus = readable(memberUser?.status, "-");
  const joinedAt = formatDateTime(membership?.createdAt) || "-";
  const updatedAt = formatDateTime(membership?.updatedAt || memberUser?.updatedAt) || "-";

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon={<UserOutlined />}
          title="Workspace Member"
          subtitle="Minimal user and membership details"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            style={workspaceButtonStyles.back}
            onClick={() => navigate(paths.workspaceDetails(workspaceId))}
          >
            Back
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {loading && !memberUser ? (
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      ) : !memberUser && !membership ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace member not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card
            style={{
              borderRadius: 8,
              borderColor: OLIVEEB_COLORS.oliveebBlueSoft,
              background: `linear-gradient(135deg, ${OLIVEEB_COLORS.card} 0%, ${OLIVEEB_COLORS.oliveebBlueSoft} 48%, ${OLIVEEB_COLORS.oliveebGreenSoft} 100%)`,
              boxShadow: "0 18px 42px rgba(9, 3, 89, 0.12)",
            }}
            bodyStyle={{ padding: 22 }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 18,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  if (photoUrl) setPhotoPreviewOpen(true);
                }}
                disabled={!photoUrl}
                aria-label="Preview profile photo"
                style={{
                  border: `3px solid ${OLIVEEB_COLORS.card}`,
                  background: "transparent",
                  padding: 0,
                  cursor: photoUrl ? "zoom-in" : "default",
                  borderRadius: "50%",
                  boxShadow: "0 14px 30px rgba(15, 23, 42, 0.18)",
                  flex: "0 0 auto",
                }}
              >
                <Avatar
                  src={photoUrl || undefined}
                  icon={!photoUrl ? <UserOutlined /> : undefined}
                  style={{
                    background: workspaceStyles.iconBubble.background,
                    color: workspaceStyles.iconBubble.color,
                    fontSize: 34,
                  }}
                  size={88}
                >
                  {!photoUrl ? String(name || "U").slice(0, 1).toUpperCase() : null}
                </Avatar>
              </button>
              <div style={{ minWidth: 220, flex: 1 }}>
                <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
                  {account?.name || "Workspace"}
                </Text>
                <Title level={3} style={{ margin: "2px 0 4px", color: OLIVEEB_COLORS.oliveebDarkBlue }}>
                  {name}
                </Title>
                <Space wrap size={[8, 6]}>
                  <Tag color={statusColor(membership?.membershipStatus)} style={{ marginInlineEnd: 0 }}>
                    {membershipStatus}
                  </Tag>
                  {userStatus && userStatus !== "-" ? (
                    <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                      {userStatus}
                    </Tag>
                  ) : null}
                  {gender ? (
                    <Tag color="green" style={{ marginInlineEnd: 0 }}>
                      {readable(gender)}
                    </Tag>
                  ) : null}
                </Space>
              </div>
              <Space direction="vertical" size={6} style={{ minWidth: 220 }}>
                {email && email !== "None" ? (
                  <Text style={{ color: OLIVEEB_COLORS.textPrimary, wordBreak: "break-word" }}>
                    <MailOutlined style={{ color: OLIVEEB_COLORS.oliveebBlue }} /> {email}
                  </Text>
                ) : null}
                {phone && phone !== "None" ? (
                  <Text style={{ color: OLIVEEB_COLORS.textPrimary, wordBreak: "break-word" }}>
                    <PhoneOutlined style={{ color: OLIVEEB_COLORS.oliveebGreen }} /> {phone}
                  </Text>
                ) : null}
              </Space>
            </div>
          </Card>

          {!membership ? (
            <Alert
              type="warning"
              showIcon
              message="Membership details are not available."
              description="Showing only the available user profile details."
            />
          ) : null}

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card
                title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>User Profile</Text>}
                style={{ borderRadius: 8, height: "100%", borderColor: OLIVEEB_COLORS.oliveebGreenSoft }}
              >
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  items={[
                    {
                      key: "email",
                      label: "Email",
                      children: email && email !== "None" ? email : "-",
                    },
                    {
                      key: "phone",
                      label: "Phone",
                      children: phone && phone !== "None" ? phone : "-",
                    },
                    {
                      key: "gender",
                      label: "Gender",
                      children: gender ? readable(gender) : "-",
                    },
                    {
                      key: "username",
                      label: "Username",
                      children: memberUser?.username || "-",
                    },
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card
                title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Workspace Details</Text>}
                style={{ borderRadius: 8, height: "100%", borderColor: OLIVEEB_COLORS.oliveebBlueSoft }}
              >
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  items={[
                    {
                      key: "workspace",
                      label: "Workspace",
                      children: account?.name || "-",
                    },
                    {
                      key: "userId",
                      label: "User ID",
                      children: userId ? (
                        <CopyableText value={userId} />
                      ) : (
                        "-"
                      ),
                    },
                    {
                      key: "joined",
                      label: "Joined",
                      children: joinedAt,
                    },
                    {
                      key: "updated",
                      label: "Updated",
                      children: updatedAt,
                    },
                  ]}
                />
              </Card>
            </Col>
          </Row>

          <Modal
            open={photoPreviewOpen}
            onCancel={() => setPhotoPreviewOpen(false)}
            footer={null}
            centered
            width={420}
            title="Profile Photo"
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={`${name} profile`}
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: 8,
                  display: "block",
                }}
              />
            ) : null}
          </Modal>

          <Card
            title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Access</Text>}
            extra={
              <Button size="small" onClick={() => navigate(paths.accessControl)}>
                Access Control
              </Button>
            }
            style={{ borderRadius: 8 }}
          >
            <Space direction="vertical" size={10} style={{ width: "100%" }}>
              <div>
                <Text type="secondary">Roles</Text>
                <div style={{ marginTop: 6 }}>
                  {roles.length ? roles.map((role) => (
                    <Tag key={role} color="blue">{readable(role)}</Tag>
                  )) : <Text>-</Text>}
                </div>
              </div>
              <div>
                <Text type="secondary">Privileges</Text>
                <div style={{ marginTop: 6 }}>
                  {privileges.length ? privileges.map((scope) => (
                    <Tag key={scope} color="cyan">{readable(scope)}</Tag>
                  )) : <Text>-</Text>}
                </div>
              </div>
            </Space>
          </Card>
        </Space>
      )}
    </div>
  );
};

export default WorkspaceMemberDetailsPage;
